/**
 * The primary actions the programmes list offers, for the top bar's slot.
 *
 * These were two links in the shell's navigation card, shown on every screen.
 * They belong to the list they start from, and the route names them there.
 *
 * @module router/programmes/ProgrammeActions
 */

import React from "react";

import { useNavigate } from "react-router";

import { Dropdown, Icon, LinkButton, type MenuItem, useMediaQuery } from "@/components/ui";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/** Below `sm`, where two controls do not fit beside everything else. */
const ONE_ACTION = "(max-width: 39.999rem)";

/**
 * "New programme", and "New module" beside it for whoever may create one.
 *
 * One labelled menu below `sm` when both are offered: the two buttons do not
 * fit beside a breadcrumb, notifications and the account on a phone, and a bare
 * plus names nothing. An account offered only one keeps that one by name.
 */
export function ProgrammeListActions(): React.ReactElement | null {
  const navigate = useNavigate();
  const { canQuickCreateProgramme, canQuickCreateModule } = useWorkspaceOutletContext();
  const narrow = useMediaQuery(ONE_ACTION);
  if (!canQuickCreateProgramme && !canQuickCreateModule) {
    return null;
  }

  // One action is shown by name at any width; only two need a menu.
  if (narrow && canQuickCreateProgramme && canQuickCreateModule) {
    const items: MenuItem[] = [
      {
        key: "new-programme",
        label: "New programme",
        icon: "plus",
        onSelect: () => {
          navigate("/programmes/new");
        },
        "data-testid": "start-new-programme",
      },
      {
        key: "new-module",
        label: "New module",
        icon: "books",
        onSelect: () => {
          navigate("/modules/new");
        },
        "data-testid": "start-new-module",
      },
    ];
    return (
      <Dropdown
        aria-label="Create"
        trigger={
          <>
            <Icon name="plus" aria-hidden /> Create
          </>
        }
        triggerAppearance="primary"
        items={items}
        data-testid="programme-create-menu"
      />
    );
  }

  return (
    <div className="flex items-center gap-2">
      {canQuickCreateModule && (
        <LinkButton to="/modules/new" size="sm" icon="books" data-testid="start-new-module">
          New module
        </LinkButton>
      )}
      {canQuickCreateProgramme && (
        <LinkButton
          to="/programmes/new"
          variant="primary"
          size="sm"
          icon="plus"
          data-testid="start-new-programme"
        >
          New programme
        </LinkButton>
      )}
    </div>
  );
}
